import React from 'react'
import {
  TextField,
  Button,
  Box,
  Snackbar,
  ListItem,
  List,
  CircularProgress,
  Paper,
} from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import LoadIngredientAutocomplete from 'shared/autocomplete/loadIngredientAutocomplete'
import API from '@aws-amplify/api'
import { useDispatch } from 'react-redux'
import UnitsSelect from 'shared/autocomplete/unitsSelect'
import { IIngredient } from 'interfaces/IIngredient'
import RecalculateStockButton from '../shared/recalculateStockButton'

export default function ManageIngredient() {
  const [ingredientId, setIngredientId] = React.useState('')
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [ingredientData, setIngredientData] = React.useState<any>(null)
  const [isLoading, setIsLoading] = React.useState<any>(false)
  const [alertMessage, setAlertMessage] = React.useState('')

  const handleIngredientChange = (ingredient: IIngredient): void => {
    setIngredientId(ingredient.ingredientId)
    setIngredientData(ingredient)
  }

  const handleIngredientFieldChange = (value: any, key: string): void => {
    setIngredientData({ ...ingredientData, [key]: value })
  }

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const saveIngredient = (e: React.SyntheticEvent): void => {
    e.preventDefault()
    setIsLoading(true)
    API.patch('products', `/ingredients/updateIngredient/${ingredientId}`, {
      body: { ...ingredientData },
    })
      .then((response) => {
        setAlertMessage('Berhasil mengubah bahan baku.')
        setIsSuccessSnackbarOpen(true)
      })
      .catch((error) => {
        // Error updating ingredient
        setAlertMessage('Gagal mengubah bahan baku.')
        setIsFailureSnackbarOpen(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Pengaturan Bahan Baku' })
  }, [dispatch])

  return (
    <Box>
      <Paper>
        <Box p={3}>
          <LoadIngredientAutocomplete
            showDetailedTable
            name={ingredientId}
            onChange={handleIngredientChange}
            showInactive
          />
        </Box>
      </Paper>
      <Box mt={3} />
      <Paper>
        <form noValidate autoComplete="off" onSubmit={saveIngredient}>
          {ingredientData && (
            <List>
              <ListItem>
                <TextField
                  required
                  autoComplete="off"
                  variant="outlined"
                  value={ingredientData.ingredientId}
                  fullWidth
                  label="Kode Bahan Baku"
                  onChange={(e) =>
                    handleIngredientFieldChange(
                      e.currentTarget.value,
                      'ingredientId'
                    )
                  }
                />
              </ListItem>
              <ListItem>
                <TextField
                  autoComplete="off"
                  variant="outlined"
                  value={ingredientData.ingredientName}
                  fullWidth
                  label="Nama Bahan Baku"
                  onChange={(e) =>
                    handleIngredientFieldChange(
                      e.currentTarget.value,
                      'ingredientName'
                    )
                  }
                />
              </ListItem>
              <ListItem>
                <UnitsSelect
                  value={ingredientData.unit || 'KG'}
                  options={['KG', 'PCS']}
                  onChange={(e) =>
                    handleIngredientFieldChange(e.target.value, 'unit')
                  }
                />
              </ListItem>
              <ListItem>
                <Box mt={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isLoading}
                  >
                    Save
                  </Button>
                  <Box component="span" ml={2}>
                    <RecalculateStockButton
                      type="ingredient"
                      id={ingredientId}
                    />
                  </Box>
                </Box>
              </ListItem>
            </List>
          )}
          {isLoading && (
            <Box p={3}>
              <CircularProgress />
            </Box>
          )}
        </form>
      </Paper>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  )
}
